import axios from "axios";
import queryString from "query-string";
import { useNavigate } from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import { BACKEND_URL, BoTokenAtom, BoUserTypeAtom, UserObjectIdAtom } from "../Atoms";

import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {isDevEnv, clearLocalStorage} from "../common/Util";
import {getImpersonationToken, isImpersonating, isWriteBlocked} from "../common/impersonation";
dayjs.extend(customParseFormat);

const useAxios = () => {
  const navigate = useNavigate();
  // const boToken = useRecoilValue(BoTokenAtom);
  // const userObjectId = useRecoilValue(UserObjectIdAtom);
  // const setBoToken = useSetRecoilState(BoTokenAtom);
  // const setBoUserType = useSetRecoilState(BoUserTypeAtom);

  //2511: ISMS 로그아웃용
  const [boToken, setBoToken] = useRecoilState(BoTokenAtom);
  const [boUserType, setBoUserType] = useRecoilState(BoUserTypeAtom);
  const [userObjectId, setUserObjectId] = useRecoilState(UserObjectIdAtom);

  const getAuth = () => {
    // [고객화면 대리열람] 대리 토큰이 주입된 경우 그것을 우선 사용 (공용 boToken 미사용, 격리)
    const impToken = getImpersonationToken();
    if (impToken) {
      return { Authorization: `Bearer ${impToken}` };
    }
    return boToken.accessToken
      ? { Authorization: `Bearer ${boToken.accessToken}` }
      : null;
  };

  // Token refresh function
  const refreshToken = async () => {
    // [고객화면 대리열람] 대리 세션은 갱신하지 않는다(단기 토큰). 만료 시 그대로 실패 처리.
    if (isImpersonating()) return null;
    try {
      const response = await axios.get(`${BACKEND_URL}/vws/bo/auth/token/refresh`, {
        headers: {
          Authorization: `Bearer ${boToken.accessToken}`,
          "Authorization-refresh": `Bearer ${boToken.refreshToken}`,
        },
      });
      
      setBoToken({
        accessToken: response.data.accessToken,
        refreshToken: boToken.refreshToken,
      });
      
      return response.data.accessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return null;
    }
  };

  // Anonymous login function
  const anonymousLogin = async () => {
    // [고객화면 대리열람] 대리 세션 중에는 익명 로그인 금지.
    // (성공 시 setBoToken 으로 공용 세션을 덮어써 격리가 깨지므로)
    if (isImpersonating()) return null;
    try {
      const response = await axios.get(`${BACKEND_URL}/vws/bo/auth/anonymous`);
      
      setBoToken({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      });
      setBoUserType("anonymous");
      
      return response.data.accessToken;
    } catch (error) {
      console.error("Anonymous login failed:", error);
      return null;
    }
  };

  const onLogout = () => {
    //ISMS.4
    if (!isDevEnv()) return; ///////////////// DEV환경만 자동로그아웃 적용. /////////////

    if (!boToken || boUserType === 'anonymous') {
      console.log('autoLogoutTimer: - onLogout: 이미 로그아웃상태임')
      return;
    }
    setBoToken("");
    setBoUserType("");
    setUserObjectId("");
    if(window.localStorage.getItem('lastGet')) {
      clearLocalStorage();
      alert((ISMS_GET_TIMEOUT / 60000) + '분 간 사용하지 않아 로그아웃됩니다.');
    }

    if(window.location.host.startsWith("sm.")) {
      window.location.href = "/b2b";
    }else {
      window.location.href = "/";
    }
  };

  //ISMS.4
  const ISMS_GET_TIMEOUT =  90 * 60 * 1000 ; //TEST 1분 or 90분.
  // const ISMS_GET_TIMEOUT = 30*60*1000 ; //30분. //상용적용 버전 -> 2511현재 isDevEnv()에서만 동작중.

  let autoLogoutTimer = undefined;

  const axiosGet = async (url, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }
    
    const makeRequest = async (headers = null) => {
      try {
        const response = await axios.get(BACKEND_URL + url, {
          headers: url.startsWith('/apiOpen') ? {} : (headers || getAuth()),
        });

        //ISMS.10 : 30분체크를 위한 localStorage에 시간기록. lastSuccess.
        if (window) {
          //디버깅 용도로 기록은 하는데, timer에선 미사용.
          window.localStorage.setItem('lastGet',  dayjs(new Date()).format('YY/MM/DD HH:mm:ss'));
          // console.log('autoLogoutTimer: lastGet', window.localStorage.getItem('lastGet'));

          // ✅ 이전 타이머가 있으면 먼저 취소
          if (autoLogoutTimer) {
            // console.log('autoLogoutTimer: 이전 타이머 취소');
            clearTimeout(autoLogoutTimer);
          }

          // ✅ 새 타이머 설정: 시간비교해서 logout or 시간비교안해도 수행되면 TIMEOUT이 맞음.
          autoLogoutTimer = setTimeout(() => {
            //passedSec  >  ISMS_GET_TIMEOUT/1000

            let lastGetTime = window.localStorage.getItem('lastGet');
            // console.log('autoLogoutTimer: signOut 타이머 수행. lastGet=', lastGetTime);

            // 시간 파싱
            const lastTime = dayjs(lastGetTime, 'YY/MM/DD HH:mm:ss');
            const now = dayjs();
            // 경과 시간 (밀리초)
            const elapsed = now.diff(lastTime);

            // 90분 지났는지 체크
            const isTimeout = elapsed > (ISMS_GET_TIMEOUT - 10*1000); //10초 마진.
            if (!isTimeout) {
              // console.log('autoLogoutTimer: !isTimeout.  ', elapsed);
              return;
            }

            onLogout();

          }, ISMS_GET_TIMEOUT);
        }


        return response.data;
      } catch (error) {
        throw error;
      }
    };

    try {
      return await makeRequest();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("401 error detected, attempting token refresh...");
        
        // Try to refresh token first
        const newToken = await refreshToken();
        if (newToken) {
          try {
            // Retry with new token
            return await makeRequest({ Authorization: `Bearer ${newToken}` });
          } catch (retryError) {
            console.error("Request failed after token refresh:", retryError);
          }
        }
        
        // If refresh failed, try anonymous login
        const anonymousToken = await anonymousLogin();
        if (anonymousToken) {
          try {
            // Retry with anonymous token
            return await makeRequest({ Authorization: `Bearer ${anonymousToken}` });
          } catch (retryError) {
            console.error("Request failed after anonymous login:", retryError);
          }
        }
        
        // If all else fails, redirect to signin
        console.error("All authentication attempts failed, redirecting to signin");
        if (
          (window.location.href.includes("localhost") ||
            window.location.href.includes("local.")) &&
          window.location.href !== "http://localhost:3030" &&
          window.location.href !== "http://b.local.com:3030/b2b"
        ) {
          navigate("/signin");
        }
      } else {
        console.error("Non-401 error:", error);
      }
      return null;
    }
  };

  const axiosGetNoAuth = async (url, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }
    try {
      const response = await axios.get(BACKEND_URL + url);
      const data = response.data;
      // console.log(data);
      return data;
    } catch (error) {
      if (error.response) {
        console.error(
          error.response.status,
          "=================NO_AUTH인데도 에러  401에러 다시 로그인해 주세요 =============================",
          window.location.href
        );
      }
      return null;
    }
  };

  const axiosPostBlob = async (url, body, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }

    const makeRequest = async (headers = null) => {
      try {

        // console.log('axiosPostBlob: url, auth ', url, getAuth());

        const response = await axios.post(BACKEND_URL + url, body, {
          responseType: 'blob',
          headers:  (headers || getAuth()),
        });

        const blob = response.data;

        // 상세 로깅: 서버 응답 정보
        const contentType = response.headers['content-type'];
        console.log('[axiosPostBlob] Response info:', {
          url: url,
          blobSize: blob.size,
          contentType: contentType,
          status: response.status
        });

        // 모든 blob의 처음 500자를 읽어서 로그 출력 (디버깅용)
        if (blob.size < 100000) { // 100KB 미만만 체크
          try {
            const clonedBlob = blob.slice(0, blob.size);
            const text = await clonedBlob.text();
            console.log('[axiosPostBlob] Response content preview (first 500 chars):', text.substring(0, 500));

            // JSON 파싱 시도
            try {
              const jsonResponse = JSON.parse(text);
              console.log('[axiosPostBlob] Parsed JSON:', jsonResponse);

              // error, message, success 등의 필드 확인
              if (jsonResponse.error || jsonResponse.message || jsonResponse.success === false) {
                console.error('[axiosPostBlob] Server returned error response:', jsonResponse);
                throw new Error(jsonResponse.message || jsonResponse.error || 'Server returned error');
              }
            } catch (parseError) {
              // JSON 파싱 실패 = 정상 파일
              console.log('[axiosPostBlob] Not a JSON response (parse failed), treating as file');
            }
          } catch (readError) {
            console.warn('[axiosPostBlob] Failed to read blob content:', readError);
          }
        }

        return blob;
      } catch (error) {
        throw error;
      }
    };

    try {
      return await makeRequest();

    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("401 error detected in POST, attempting token refresh...");

        // Try to refresh token first
        const newToken = await refreshToken();
        if (newToken) {
          try {
            // Retry with new token
            return await makeRequest({Authorization: `Bearer ${newToken}`});
          } catch (retryError) {
            console.error("POST request failed after token refresh:", retryError);
            return null;
          }
        }
      }
      console.error("axiosPostBlob error:", error);
      return null;
    }

  };

  const axiosPost = async (url, body, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }

    // [고객화면 대리열람] 읽기전용 모드에서 쓰기(POST) 차단. (allowlist 조회 POST는 통과)
    if (isWriteBlocked(url)) {
      console.warn("[대리열람] 읽기전용 모드: POST 차단", url);
      if (typeof window !== "undefined") window.alert("열람 전용 모드입니다. 변경은 저장되지 않습니다.");
      return null;
    }

    const makeRequest = async (headers = null) => {
      try {
        const response = await axios.post(BACKEND_URL + url, body, {
          headers: url.startsWith('/apiOpen') ? {} : (headers || getAuth()),
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    };

    try {
      return await makeRequest();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("401 error detected in POST, attempting token refresh...");
        
        // Try to refresh token first
        const newToken = await refreshToken();
        if (newToken) {
          try {
            // Retry with new token
            return await makeRequest({ Authorization: `Bearer ${newToken}` });
          } catch (retryError) {
            console.error("POST request failed after token refresh:", retryError);
          }
        }
        
        // If refresh failed, try anonymous login
        const anonymousToken = await anonymousLogin();
        if (anonymousToken) {
          try {
            // Retry with anonymous token
            return await makeRequest({ Authorization: `Bearer ${anonymousToken}` });
          } catch (retryError) {
            console.error("POST request failed after anonymous login:", retryError);
          }
        }
        
        // If all else fails, redirect to signin
        console.error("All authentication attempts failed for POST, redirecting to signin");
        alert("다시 로그인해 주세요");
        navigate("/signin");
      } else {
        console.error("Non-401 error in POST:", error);
      }
      return null;
    }
  };

  const axiosPut = async (url, body, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }

    // [고객화면 대리열람] 읽기전용 모드에서 쓰기(PUT) 차단.
    if (isWriteBlocked(url)) {
      console.warn("[대리열람] 읽기전용 모드: PUT 차단", url);
      if (typeof window !== "undefined") window.alert("열람 전용 모드입니다. 변경은 저장되지 않습니다.");
      return null;
    }

    const makeRequest = async (headers = null) => {
      try {
        const response = await axios.put(BACKEND_URL + url, body, {
          headers: url.startsWith('/apiOpen') ? {} : (headers || getAuth()),
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    };

    try {
      return await makeRequest();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("401 error detected in PUT, attempting token refresh...");
        
        // Try to refresh token first
        const newToken = await refreshToken();
        if (newToken) {
          try {
            // Retry with new token
            return await makeRequest({ Authorization: `Bearer ${newToken}` });
          } catch (retryError) {
            console.error("PUT request failed after token refresh:", retryError);
          }
        }
        
        // If refresh failed, try anonymous login
        const anonymousToken = await anonymousLogin();
        if (anonymousToken) {
          try {
            // Retry with anonymous token
            return await makeRequest({ Authorization: `Bearer ${anonymousToken}` });
          } catch (retryError) {
            console.error("PUT request failed after anonymous login:", retryError);
          }
        }
        
        // If all else fails, redirect to signin
        console.error("All authentication attempts failed for PUT, redirecting to signin");
        alert("다시 로그인해 주세요");
        navigate("/signin");
      } else {
        console.error("Non-401 error in PUT:", error);
      }
      return null;
    }
  };

  const axiosDelete = async (url, params) => {
    if (params) {
      url = `${url}?${queryString.stringify(params)}`;
    }

    // [고객화면 대리열람] 읽기전용 모드에서 쓰기(DELETE) 차단.
    if (isWriteBlocked(url)) {
      console.warn("[대리열람] 읽기전용 모드: DELETE 차단", url);
      if (typeof window !== "undefined") window.alert("열람 전용 모드입니다. 변경은 저장되지 않습니다.");
      return null;
    }

    const makeRequest = async (headers = null) => {
      try {
        const response = await axios.delete(BACKEND_URL + url, {
          headers: url.startsWith('/apiOpen') ? {} : (headers || getAuth()),
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    };

    try {
      const result = await makeRequest();
      console.log("DEL RESULT,", result);
      return result;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("401 error detected in DELETE, attempting token refresh...");
        
        // Try to refresh token first
        const newToken = await refreshToken();
        if (newToken) {
          try {
            // Retry with new token
            return await makeRequest({ Authorization: `Bearer ${newToken}` });
          } catch (retryError) {
            console.error("DELETE request failed after token refresh:", retryError);
          }
        }
        
        // If refresh failed, try anonymous login
        const anonymousToken = await anonymousLogin();
        if (anonymousToken) {
          try {
            // Retry with anonymous token
            return await makeRequest({ Authorization: `Bearer ${anonymousToken}` });
          } catch (retryError) {
            console.error("DELETE request failed after anonymous login:", retryError);
          }
        }
        
        // If all else fails, redirect to signin
        console.error("All authentication attempts failed for DELETE, redirecting to signin");
        alert("다시 로그인해 주세요");
        navigate("/signin");
      } else {
        console.error("Non-401 error in DELETE:", error);
      }
      return null;
    }
  };

  return { axiosGet, axiosPost, axiosPut, axiosDelete, axiosGetNoAuth, axiosPostBlob };
};

export default useAxios;
